import {
  CreateEditDtoSourceTypeEnum2,
  EnrichedVideoEditorEditListItemDto,
} from '@app/modules/compose/composition.types';
import { useCompositionVideoEditionsEditsQuery } from '@app/modules/compose/hooks/useCompositionVideoEditionsEditsQuery';
import {
  CloneVideoEditionEditDialog,
  VideoEditionsEditList,
  VideoEditionsEditListSkeleton,
  VideoEditionsEditsEmptyState,
  useVideoEditionQuickCreate,
} from '@app/modules/video-editions';
import { getCompletedPublishedPlatformsSummary } from '@app/modules/video-editions/lib/get-completed-published-platforms-summary';
import { webAppGetCompositionsDetailEditsQueryKey } from '@mi-front/api-v2';
import { useQueryClient } from '@tanstack/react-query';
import { QueryView } from '@ui/utils/async-view/QueryView';
import { useState } from 'react';
import { CompositionVideoEditionPublishMenu } from './CompositionVideoEditionPublishMenu';
import { useComposition } from '../hooks/useComposition';

/**
 * Editions tab for a composition: same video-editions flows as fragments, including generated-edit publish menu.
 */
export function CompositionVideoEditionsTab() {
  const { composition } = useComposition();
  const queryClient = useQueryClient();
  const [cloneEdit, setCloneEdit] = useState<EnrichedVideoEditorEditListItemDto | null>(null);
  const listQuery = useCompositionVideoEditionsEditsQuery(composition.id);

  const { createWithAutoName, isCreating } = useVideoEditionQuickCreate({
    buildCreatePayload: (name) => ({
      sourceType: CreateEditDtoSourceTypeEnum2.COMPOSITION,
      sourceId: composition.id,
      name,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: webAppGetCompositionsDetailEditsQueryKey(composition.id),
      });
    },
  });

  const invalidateEdits = async () => {
    await queryClient.invalidateQueries({
      queryKey: webAppGetCompositionsDetailEditsQueryKey(composition.id),
    });
  };

  return (
    <>
      <QueryView query={listQuery} loader={<VideoEditionsEditListSkeleton />}>
        {(editsFromQuery) =>
          !editsFromQuery.length ? (
            <VideoEditionsEditsEmptyState
              createWithAutoName={createWithAutoName}
              isCreating={isCreating}
            />
          ) : (
            <VideoEditionsEditList
              edits={editsFromQuery}
              onCloneEdit={(edit) => setCloneEdit(edit)}
              onEditMutationSuccess={invalidateEdits}
              renderPublishControl={(edit) => {
                const { count: completedPublishCount } = getCompletedPublishedPlatformsSummary(
                  edit.publishedPlatforms,
                );
                return (
                  <CompositionVideoEditionPublishMenu
                    editId={edit.id}
                    compositionId={composition.id}
                    stationId={composition.stationId ?? undefined}
                    iconOnly={completedPublishCount > 0}
                  />
                );
              }}
              createWithAutoName={createWithAutoName}
              isCreating={isCreating}
            />
          )
        }
      </QueryView>

      {cloneEdit && (
        <CloneVideoEditionEditDialog
          open={!!cloneEdit}
          onClose={() => setCloneEdit(null)}
          sourceEdit={cloneEdit}
          onSuccess={invalidateEdits}
        />
      )}
    </>
  );
}
